import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import { reportsAPI } from '../../services/api';
import { useTheme } from '../../context/ThemeContext';
import HelpWidget from './HelpWidget';
import NotificationBell from './NotificationBell';
import logo from '../../assets/logo.png';

const REPORTS_PAGE_SIZE = 14;

import './Layout.css';

const NAV_LINKS = [
  { path: '/dashboard', label: 'Dashboard', Icon: DashboardOutlinedIcon },
  { path: '/reports/new', label: 'New Report', Icon: NoteAddOutlinedIcon },
  { path: '/reports', label: 'All Reports', Icon: ArticleOutlinedIcon },
  { path: '/feedback', label: 'Feedback', Icon: FeedbackOutlinedIcon },
];

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const initials = `${user?.first_name?.[0] || ''}${user?.last_name?.[0] || ''}`.toUpperCase();
  const { dark, toggle: toggleTheme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const dropRef = useRef(null);

  useEffect(() => {
    const handler = e => { if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const prefetchReports = (path) => {
    if (path !== '/reports') return;
    reportsAPI.getAll({ page: 1, limit: REPORTS_PAGE_SIZE }).catch(() => { });
  };

  return (
    <div className={`layout ${collapsed ? "sidebar-collapsed" : ""}`}>
      <aside className="sidebar">
        <div className="sidebar-brand">
          <div className="sidebar-brand-icon"><img src={logo} alt="Umedex QC" className="sidebar-brand-logo" /></div>
          <div className="sidebar-brand-text">
            <span className="sidebar-brand-main">UMEDEX</span>
            <span className="sidebar-brand-sub">QC Reports</span>
          </div>
        </div>

        <nav className="sidebar-nav">
          {NAV_LINKS.map(({ path, label, Icon }) => (
            <Link key={path} to={path} onMouseEnter={() => prefetchReports(path)} className={`sidebar-link ${location.pathname === path ? 'active' : ''}`}>
              <Icon sx={{ fontSize: 18, flexShrink: 0 }} />
              <span>{label}</span>
            </Link>
          ))}
        </nav>


      </aside>

      <div className="main-wrapper">
        {/* Top bar */}
        <header className="topbar">
          <div className="topbar-left">
            <button className="sidebar-toggle" onClick={() => setCollapsed(c => !c)} title={collapsed ? "Expand sidebar" : "Collapse sidebar"}>
              <MenuIcon sx={{ fontSize: 18 }} />
            </button>
          </div>
          <div className="topbar-right">
            <NotificationBell />
            <button className="topbar-theme-btn" onClick={toggleTheme} title="Toggle theme">
              {dark ? <LightModeIcon sx={{ fontSize: 17 }} /> : <DarkModeIcon sx={{ fontSize: 17 }} />}
            </button>
            <div className="profile-wrap" ref={dropRef}>
              <button className="profile-btn" onClick={() => setDropOpen(d => !d)}>
                <div className="profile-av">{initials || '?'}</div>
                <div className="profile-info">
                  <span className="profile-name">{user?.first_name} {user?.last_name}</span>
                </div>
                {/* <span className="profile-chevron">{dropOpen ? '▲' : '▼'}</span> */}
              </button>

              {dropOpen && (
                <div className="profile-dropdown">
                  <button className="pd-item" onClick={() => { setDropOpen(false); navigate('/profile'); }}>
                    <ManageAccountsOutlinedIcon sx={{fontSize:15}}/> View Profile
                  </button>
                  {/* <button className="pd-item" onClick={() => { setDropOpen(false); navigate('/profile'); }}>
                    <SettingsOutlinedIcon sx={{fontSize:15}}/> Settings
                  </button> */}
                  <div className="pd-divider" />
                  <button className="pd-item pd-logout" onClick={async () => { setDropOpen(false); await logout(); navigate('/login'); }}>
                    <LogoutIcon sx={{ fontSize: 15 }} /> Log out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="main-content">{children}</main>
      </div>
      <HelpWidget />
    </div>
  );
};

export default Layout;